import { useState, useEffect, useCallback } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import { Task } from '@medplum/fhirtypes';
import { searchPendingDiagnosticReportReviewTasks } from '../utils/fhir-helpers';

// -----------------------------------------------------------------------------
export interface UseReviewTasksProps {
  diagnosticReportId?: string;
}

// -----------------------------------------------------------------------------
export const useReviewTasks = ({ diagnosticReportId }: UseReviewTasksProps) => {
  const medplum = useMedplum();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);

  // ---------------------------------------------------------------------------
  const loadTasks = useCallback(async () => {
    if (!diagnosticReportId) {
      setTasks([]);
      return;
    }

    setLoading(true);
    try {
      const pendingTasks = await searchPendingDiagnosticReportReviewTasks(
        medplum,
        diagnosticReportId
      );
      setTasks(pendingTasks);
    } catch (error) {
      console.error('Error loading review tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [medplum, diagnosticReportId]);

  // ---------------------------------------------------------------------------
  // Initial load
  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // ---------------------------------------------------------------------------
  // Subscribe to Task updates for this diagnostic report
  useSubscription(
    diagnosticReportId
      ? `Task?focus=DiagnosticReport/${diagnosticReportId}`
      : '',
    (bundle) => {
      const updatedTask = bundle.entry?.find(
        (e) => e.resource?.resourceType === 'Task'
      )?.resource as Task | undefined;

      if (!updatedTask) {
        return;
      }

      // Update the tasks list
      setTasks((prev) => {
        const exists = prev.find((t) => t.id === updatedTask.id);

        // Filter to only show pending tasks (requested or in-progress)
        const isPending =
          updatedTask.status === 'requested' ||
          updatedTask.status === 'in-progress';

        if (exists) {
          // Update existing task
          if (isPending) {
            return prev.map((t) => (t.id === updatedTask.id ? updatedTask : t));
          } else {
            // Task is no longer pending (completed/rejected), remove it
            return prev.filter((t) => t.id !== updatedTask.id);
          }
        } else if (isPending) {
          // Add new pending task
          return [...prev, updatedTask];
        }

        return prev;
      });
    }
  );

  // ---------------------------------------------------------------------------
  return {
    tasks,
    loading,
    reload: loadTasks,
    hasTasks: tasks.length > 0,
  };
};
